import { defineStore } from 'pinia';
import { MESSAGE_TYPE, MESSAGE_STATUS } from "../api/enums.js"
import useUserStore from './userStore.js';
import useFriendStore from './friendStore.js';
import useGroupStore from './groupStore.js';
import localForage from 'localforage';
import yunxinIM from '../api/yunxinIM.js'

/**
 * 优化1(冷热消息分区):
 * 热消息：登录后的消息
 * 冷消息: 登录前的消息
 * 每个会话的冷热消息分别用一个key进行存储，当有新的消息时，只更新热消息key，冷消息key保持不变
 * 由于热消息数量不会很大，所以localForage.setItem耗时很低，可以防止消息过多时出现卡顿的情况
 *
 * 优化2(延迟渲染):
 * 拉取消息时，如果直接用state.chats接收，页面就开始渲染，一边渲染页面一边大量接消息会导致很严重的卡顿
 * 为了加速拉取离线消息效率，拉取时消息暂时存储到cacheChats,等待所有离线消息拉取完成后，再统一放至state中进行渲染
 * 
 * 优化3(pinia代替vuex)
 * 实测pinia的远超vuex,且语法更简洁清晰
 * 
 * */


let cacheChats = [];
let cloudConversationIds = new Set();

function getChatSortTime(chat = {}) {
	return Number(chat.optTime || chat.lastSendTime || 0);
}

function getFriendAliases(targetId) {
	const aliases = new Set([targetId].filter(Boolean).map(item => String(item)));
	const friendStore = useFriendStore();
	const friend = friendStore.findFriend(targetId);
	if (friend) {
		[friend.id, friend.accountId, friend.imAccid].filter(Boolean).forEach(item => aliases.add(String(item)));
	}
	return aliases;
}

function getPrivateConversationTarget(chatInfo = {}) {
	const friendStore = useFriendStore();
	const friend = friendStore.findFriend(chatInfo.targetId);
	return friend?.imAccid || friend?.accountId || chatInfo.targetId;
}

function getChatConversationId(chatInfo = {}) {
	if (chatInfo.conversationId) {
		return chatInfo.conversationId;
	}
	if (chatInfo.type === 'SYSTEM') {
		return '';
	}
	const scene = chatInfo.type === 'GROUP' ? 'team' : 'p2p';
	const targetId = chatInfo.type === 'PRIVATE' ? getPrivateConversationTarget(chatInfo) : chatInfo.targetId;
	return yunxinIM.getConversationId(targetId, scene);
}

function isSameChat(left = {}, right = {}) {
	if (!left || !right || left.type !== right.type) {
		return false;
	}
	if (left.conversationId && right.conversationId && String(left.conversationId) === String(right.conversationId)) {
		return true;
	}
	if (left.type === 'PRIVATE') {
		const leftAliases = getFriendAliases(left.targetId);
		const rightAliases = getFriendAliases(right.targetId);
		return [...leftAliases].some(alias => rightAliases.has(alias));
	}
	return String(left.targetId) === String(right.targetId);
}

function isCloudChat(chat = {}) {
	return !!chat.conversationId || !!chat.sourceConversation;
}

function isUnknownChatName(chatInfo = {}) {
	return !chatInfo.showName || chatInfo.showName === '未知群聊' || chatInfo.showName === '未知用户' || chatInfo.showName === String(chatInfo.targetId);
}

function shouldFillPrivateProfile(chat = {}) {
	return chat.type === 'PRIVATE' && chat.targetId && (!chat.headImage || isUnknownChatName(chat));
}

function isCloudConversationChat(chat = {}) {
	return chat.type === 'SYSTEM' || [...cloudConversationIds].some(conversationId => {
		const target = {
			conversationId,
			targetId: yunxinIM.parseConversationTargetId(conversationId),
			type: Number(yunxinIM.parseConversationType(conversationId)) === 2 ? 'GROUP' : 'PRIVATE'
		};
		return isSameChat(chat, target);
	});
}

export default defineStore('chatStore', {
	state: () => {
		return {
			activeChat: null,
			chats: [],
			privateMsgMaxId: 0,
			groupMsgMaxId: 0,
			systemMsgMaxSeqNo: 0,
			loading: false
		}
	},
	actions: {
		initChats(chatsData) {
			this.chats = [];
			this.activeChat = null;
			this.privateMsgMaxId = chatsData.privateMsgMaxId || 0;
			this.groupMsgMaxId = chatsData.groupMsgMaxId || 0;
			this.systemMsgMaxSeqNo = chatsData.systemMsgMaxSeqNo || 0;
			cacheChats = (chatsData.chats || []).filter(chat => isCloudChat(chat));
			cloudConversationIds = new Set(cacheChats.map(chat => chat.conversationId).filter(Boolean).map(id => String(id)));
		},
		openChat(chatInfo, options = {}) {
			chatInfo = this.normalizeChatInfo(chatInfo);
			let chats = this.findChats()
			let chat = this.findChat(chatInfo);
			const conversationId = getChatConversationId(chatInfo);
			const syncCloud = options.syncCloud !== false;
			const strictCloud = options.strictCloud === true;
			if (syncCloud && conversationId) {
				yunxinIM.ensureCloudConversation(conversationId).catch(error => {
					console.warn('[chatStore.openChat] 创建云端会话失败:', conversationId, error);
				});
			}
			// 创建会话
			if (chat == null) {
				if (strictCloud && !isCloudConversationChat({ ...chatInfo, conversationId })) {
					return null;
				}
				chat = {
					conversationId,
					targetId: chatInfo.targetId,
					type: chatInfo.type,
					showName: chatInfo.showName,
					headImage: chatInfo.headImage,
					companyName: chatInfo.companyName,
					isDnd: chatInfo.isDnd,
					isTop: chatInfo.isTop,
					lastContent: "",
					lastSendTime: new Date().getTime(),
					optTime: new Date().getTime(),
					unreadCount: 0,
					hotMinIdx: 0,
					readedMessageIdx: 0,
					lastReadTime: 0,
					messages: [],
					atMe: false,
					atAll: false,
					lastAtMessageId: -1,
					stored: false,
					delete: false
				};
				chats.push(chat);
			} else {
				Object.assign(chat, {
					conversationId: chat.conversationId || conversationId,
					showName: isUnknownChatName(chatInfo) ? chat.showName : chatInfo.showName,
					headImage: chatInfo.headImage || chat.headImage,
					companyName: chatInfo.companyName || chat.companyName,
					isDnd: chatInfo.isDnd ?? chat.isDnd,
					isTop: chatInfo.isTop ?? chat.isTop,
					delete: false
				});
			}
			this.moveTop(chatInfo);
			return chat;
		},
		setActiveChat(chatInfo) {
			this.activeChat = this.findChat(chatInfo);
		},
		resetUnreadCount(chatInfo) {
			let chat = this.findChat(chatInfo);
			chat.unreadCount = 0;
			chat.stored = false;
			this.saveToStorage();
		},
		resetAtMessage(chatInfo) {
			let chat = this.findChat(chatInfo);
			chat.atAll = false;
			chat.atMe = false;
			chat.lastAtMessageId = -1;
			chat.stored = false;
			this.saveToStorage();
		},
		readedMessage(pos) {
			let chat = this.findChatByFriend(pos.friendId);
			if (!chat) return;
			// 已读状态偶尔异常，目前找不到原因，暂时通过回溯更新1000条的方式解决
			let idx = Math.max(0, chat.readedMessageIdx - 1000);
			for (; idx < chat.messages.length; idx++) {
				let m = chat.messages[idx];
				if (m.id && m.selfSend && m.status < MESSAGE_STATUS.RECALL) {
					// pos.maxId为空表示整个会话已读
					if (!pos.maxId || m.id <= pos.maxId) {
						m.status = MESSAGE_STATUS.READED
						chat.readedMessageIdx = idx;
						chat.stored = false;
					}
				}
			}
			if (!chat.stored) {
				this.saveToStorage();
			}
		},
		readedMessageByTime(pos) {
			const readTime = Number(pos.timestamp || pos.readTime || 0);
			if (!readTime) return;
			let chat = this.findChatByFriend(pos.friendId);
			if (!chat) return;
			const prevReadTime = Number(chat.lastReadTime || 0);
			chat.lastReadTime = Math.max(prevReadTime, readTime);
			let updated = chat.lastReadTime !== prevReadTime;
			for (let idx = 0; idx < chat.messages.length; idx++) {
				let m = chat.messages[idx];
				let msgTime = Number(m.sendTime || m.createTime || m.time || 0);
				if (m.selfSend && msgTime > 0 && msgTime <= chat.lastReadTime &&
					m.status !== MESSAGE_STATUS.READED && m.status !== MESSAGE_STATUS.RECALL) {
					m.status = MESSAGE_STATUS.READED;
					chat.readedMessageIdx = idx;
					updated = true;
				}
			}
			if (updated) {
				chat.stored = false;
				this.saveToStorage();
			}
		},
		cleanMessage(chatInfo) {
			let chat = this.findChat(chatInfo);
			if (!chat) {
				return;
			}
			chat.lastContent = '';
			chat.hotMinIdx = 0;
			chat.unreadCount = 0;
			chat.atMe = false;
			chat.atAll = false;
			chat.lastAtMessageId = -1
			chat.stored = false;
			chat.messages = [];
			chat.readedMessageIdx = 0;
			this.saveToStorage(true);
		},
		async removeChat(chatInfo) {
			let chat = this.findChat(chatInfo);
			if (!chat) {
				return;
			}
			const deleteChat = Object.assign({}, chat);
			await yunxinIM.deleteCloudConversation(deleteChat);
			this.markChatsDeleted(deleteChat);
			this.saveToStorage()
		},
		removeChatsByConversationIds(conversationIds = []) {
			const idSet = new Set(conversationIds.filter(Boolean).map(id => String(id)));
			if (!idSet.size) {
				return;
			}
			let removed = false;
			const targets = [...idSet].map(conversationId => {
				const targetId = yunxinIM.parseConversationTargetId(conversationId);
				const type = Number(yunxinIM.parseConversationType(conversationId)) === 2 ? 'GROUP' : 'PRIVATE';
				return { conversationId, targetId, type };
			});
			this.findChats().forEach(chat => {
				if (targets.some(target => isSameChat(chat, target))) {
					removed = this.markChatDeleted(chat) || removed;
				}
			});
			targets.forEach(target => {
				cloudConversationIds.delete(String(target.conversationId));
			});
			if (removed) {
				this.saveToStorage();
			}
		},
		markChatsDeleted(chatInfo) {
			let removed = false;
			const conversationId = getChatConversationId(chatInfo);
			if (conversationId) {
				cloudConversationIds.delete(String(conversationId));
			}
			this.findChats().forEach(chat => {
				if (isSameChat(chat, chatInfo)) {
					removed = this.markChatDeleted(chat) || removed;
				}
			});
			return removed;
		},
		markChatDeleted(chat) {
			if (!chat || chat.delete) {
				return false;
			}
			if (chat == this.activeChat) {
				this.activeChat = null;
			}
			chat.delete = true;
			chat.stored = false;
			return true;
		},
		normalizeChatInfo(chatInfo = {}) {
			if (chatInfo.type !== 'PRIVATE') {
				return chatInfo;
			}
			const friendStore = useFriendStore();
			const friend = friendStore.findFriend(chatInfo.targetId);
			if (!friend) {
				return chatInfo;
			}
			return {
				...chatInfo,
				targetId: friend.id || chatInfo.targetId,
				showName: chatInfo.showName || friend.showNickName,
				headImage: chatInfo.headImage || friend.headImage,
				companyName: chatInfo.companyName || friend.companyName,
				isDnd: chatInfo.isDnd ?? friend.isDnd,
				isTop: chatInfo.isTop ?? friend.isTop
			};
		},
		removePrivateChat(friendId) {
			let chat = this.findChatByFriend(friendId);
			chat && this.removeChat(chat);
		},
		removeGroupChat(groupId) {
			let chat = this.findChatByGroup(groupId);
			chat && this.removeChat(chat);
		},
		moveTop(chatInfo) {
			// 加载中不移动，很耗性能
			if (this.loading) {
				return;
			}
			let idx = this.findChatIdx(chatInfo);
			let chats = this.findChats();
			let chat = chats[idx];
			if (!chat) {
				return;
			}
			let insertIdx = chat.isTop ? 0 : this.topSize();
			if (idx != insertIdx) {
				chats.splice(idx, 1);
				chats.splice(insertIdx, 0, chat);
				chat.optTime = new Date().getTime();
				chat.stored = false;
				this.saveToStorage();
			}
		},
		insertMessage(msgInfo, chatInfo, options = {}) {
			let type = chatInfo.type;
			let chat = this.findChat(chatInfo);
			if (!chat) {
				return;
			}
			// 记录消息的最大id
			if (msgInfo.id && type == "PRIVATE" && msgInfo.id > this.privateMsgMaxId) {
				this.privateMsgMaxId = msgInfo.id;
			}
			if (msgInfo.id && type == "GROUP" && msgInfo.id > this.groupMsgMaxId) {
				this.groupMsgMaxId = msgInfo.id;
			}
			if (msgInfo.seqNo && type == "SYSTEM" && msgInfo.seqNo > this.systemMsgMaxSeqNo) {
				this.systemMsgMaxSeqNo = msgInfo.seqNo;
			}
			// 如果是已存在消息，则覆盖旧的消息数据
			let message = this.findMessage(chat, msgInfo);
			if (message) {
				Object.assign(message, msgInfo);
				chat.stored = false;
				this.saveToStorage();
				return;
			}
			// 插入新的数据
			if (msgInfo.type == MESSAGE_TYPE.IMAGE) {
				chat.lastContent = "[图片]";
			} else if (msgInfo.type == MESSAGE_TYPE.VIDEO) {
				chat.lastContent = "[视频]";
			} else if (msgInfo.type == MESSAGE_TYPE.FILE) {
				chat.lastContent = "[文件]";
			} else if (msgInfo.type == MESSAGE_TYPE.AUDIO) {
				chat.lastContent = "[语音]";
			} else if (msgInfo.type == MESSAGE_TYPE.USER_CARD) {
				chat.lastContent = "[个人名片] " + JSON.parse(msgInfo.content).nickName;
			} else if (msgInfo.type == MESSAGE_TYPE.GROUP_CARD) {
				chat.lastContent = "[群名片] " + JSON.parse(msgInfo.content).groupName;
			} else if (msgInfo.type == MESSAGE_TYPE.ACT_RT_VOICE) {
				chat.lastContent = "[语音通话]";
			} else if (msgInfo.type == MESSAGE_TYPE.ACT_RT_VIDEO) {
				chat.lastContent = "[视频通话]";
			} else if (msgInfo.type == MESSAGE_TYPE.SYSTEM_MESSAGE) {
				chat.lastContent = msgInfo.title;
			} else if (msgInfo.type == MESSAGE_TYPE.TEXT ||
				msgInfo.type == MESSAGE_TYPE.RECALL ||
				msgInfo.type == MESSAGE_TYPE.TIP_TEXT) {
				chat.lastContent = msgInfo.content;
			}
			chat.lastSendTime = msgInfo.sendTime;
			chat.sendNickName = msgInfo.sendNickName;
			chat.optTime = msgInfo.sendTime;
			// 未读加1
			if (!options.skipUnreadCount && !msgInfo.selfSend && msgInfo.status != MESSAGE_STATUS.READED &&
				msgInfo.status != MESSAGE_STATUS.RECALL && msgInfo.type != MESSAGE_TYPE.TIP_TEXT) {
				chat.unreadCount++;
			}
			// 是否有人@我
			if (!msgInfo.selfSend && chat.type == "GROUP" && msgInfo.atUserIds &&
				msgInfo.status != MESSAGE_STATUS.READED) {
				const userStore = useUserStore();
				let userId = userStore.userInfo.id;
				if (msgInfo.atUserIds.indexOf(userId) >= 0) {
					chat.atMe = true;
					chat.lastAtMessageId = msgInfo.id;
				}
				if (msgInfo.atUserIds.indexOf(-1) >= 0) {
					chat.atAll = true;
					chat.lastAtMessageId = msgInfo.id;
				}
			}
			// 间隔大于10分钟插入时间显示
			if (!chat.lastTimeTip || (chat.lastTimeTip < msgInfo.sendTime - 600 * 1000)) {
				chat.messages.push({
					sendTime: msgInfo.sendTime,
					type: MESSAGE_TYPE.TIP_TIME,
				});
				chat.lastTimeTip = msgInfo.sendTime;
			}
			chat.messages.push(msgInfo);
			chat.stored = false;
			this.saveToStorage();
		},
		updateMessage(msgInfo, chatInfo) {
			// 获取对方id或群id
			let chat = this.findChat(chatInfo);
			if (!chat) {
				return;
			}
			let message = this.findMessage(chat, msgInfo);
			if (message) {
				// 属性拷贝
				Object.assign(message, msgInfo);
				chat.stored = false;
				this.saveToStorage();
			}
		},
		deleteMessage(msgInfo, chatInfo) {
			let isColdMessage = false;
			let chat = this.findChat(chatInfo);
			let delIdx = -1;
			for (let idx in chat.messages) {
				// 已经发送成功的，根据id删除
				if (chat.messages[idx].id && chat.messages[idx].id == msgInfo.id) {
					delIdx = idx;
					break;
				}
				// 正在发送中的消息可能没有id，只有临时id
				if (chat.messages[idx].tmpId && chat.messages[idx].tmpId == msgInfo.tmpId) {
					delIdx = idx;
					break;
				}
			}
			if (delIdx >= 0) {
				chat.messages.splice(delIdx, 1);
				if (delIdx < chat.hotMinIdx) {
					isColdMessage = true;
					chat.hotMinIdx--;
				}
				if (delIdx < chat.readedMessageIdx) {
					chat.readedMessageIdx--;
				}
				chat.stored = false;
				this.saveToStorage(isColdMessage);
			}
		},
		recallMessage(msgInfo, chatInfo) {
			let chat = this.findChat(chatInfo);
			if (!chat) return;
			let isColdMessage = false;
			// 要撤回的消息id
			let id = msgInfo.content;
			let name = msgInfo.selfSend ? '你' : chat.type == 'PRIVATE' ? '对方' : msgInfo.sendNickName;
			for (let idx in chat.messages) {
				let m = chat.messages[idx];
				if (m.id && m.id == id) {
					// 不是自己撤回，说明是被管理员撤回
					if (m.sendId != msgInfo.sendId) {
						name = "管理员"
					}
					// 改造成一条提示消息
					m.status = MESSAGE_STATUS.RECALL;
					m.content = name + "撤回了一条消息";
					m.type = MESSAGE_TYPE.TIP_TEXT
					// 会话列表
					chat.lastContent = m.content;
					chat.lastSendTime = msgInfo.sendTime;
					chat.sendNickName = '';
					if (!msgInfo.selfSend && msgInfo.status != MESSAGE_STATUS.READED) {
						chat.unreadCount++;
					}
					isColdMessage = idx < chat.hotMinIdx;
				}
				// 被引用的消息也要撤回
				if (m.quoteMessage && m.quoteMessage.id == id) {
					m.quoteMessage.content = "引用内容已撤回";
					m.quoteMessage.status = MESSAGE_STATUS.RECALL;
					m.quoteMessage.type = MESSAGE_TYPE.TIP_TEXT
				}
			}
			chat.stored = false;
			this.saveToStorage(isColdMessage);
		},
		updateChatFromFriend(friend) {
			let chat = this.findChatByFriend(friend.id);
			// 更新会话中的昵称和头像
			if (chat && (chat.headImage != friend.headImage ||
				chat.showName != friend.showNickName)) {
				chat.headImage = friend.headImage;
				chat.showName = friend.showNickName;
				chat.stored = false;
				this.saveToStorage();
			}
		},
		updateChatFromUser(user) {
			let chat = this.findChatByFriend(user.id);
			// 更新会话中的昵称和头像
			if (chat && (chat.headImage != user.headImageThumb ||
				chat.showName != user.nickName)) {
				chat.headImage = user.headImageThumb;
				chat.showName = user.nickName;
				chat.stored = false;
				this.saveToStorage();
			}
		},
		async fillPrivateProfiles(chats = this.findChats()) {
			const targets = chats.filter(shouldFillPrivateProfile);
			if (!targets.length) {
				return;
			}
			const friendStore = useFriendStore();
			const accountIds = [...new Set(targets.map(chat => {
				const friend = friendStore.findFriend(chat.targetId);
				return friend?.imAccid || friend?.accountId || chat.targetId;
			}).filter(Boolean).map(item => String(item)))];
			if (!accountIds.length) {
				return;
			}
			const profiles = await yunxinIM.getUserProfiles(accountIds).catch(error => {
				console.warn('[chatStore.fillPrivateProfiles] 补全私聊资料失败:', error);
				return [];
			});
			if (!profiles.length) {
				return;
			}
			const profileMap = profiles.reduce((map, profile) => {
				[profile.id, profile.accountId, profile.imAccid].filter(Boolean).forEach(id => {
					map[String(id)] = profile;
				});
				return map;
			}, {});
			let updated = false;
			targets.forEach(chat => {
				const friend = friendStore.findFriend(chat.targetId);
				const accountId = String(friend?.imAccid || friend?.accountId || chat.targetId);
				const profile = profileMap[accountId] || profileMap[String(chat.targetId)];
				if (!profile) {
					return;
				}
				const showName = profile.showNickName || profile.nickName || profile.userName || profile.accountId;
				const headImage = profile.headImageThumb || profile.headImage || '';
				if (isUnknownChatName(chat) && showName && chat.showName !== showName) {
					chat.showName = showName;
					updated = true;
				}
				if (!chat.headImage && headImage) {
					chat.headImage = headImage;
					updated = true;
				}
				const nextFriend = {
					...profile,
					id: friend?.id || profile.id || profile.accountId,
					isDnd: friend?.isDnd ?? profile.isDnd ?? false,
					isTop: friend?.isTop ?? profile.isTop ?? chat.isTop ?? false
				};
				friend ? friendStore.updateFriend(nextFriend) : friendStore.addFriend(nextFriend);
			});
			if (updated) {
				targets.forEach(chat => {
					chat.stored = false;
				});
				this.saveToStorage();
			}
		},
		updateChatFromGroup(group) {
			let chat = this.findChatByGroup(group.id);
			if (chat && (chat.headImage != group.headImageThumb ||
				chat.showName != group.showGroupName)) {
				// 更新会话中的群名称和头像
				chat.headImage = group.headImageThumb;
				chat.showName = group.showGroupName;
				chat.stored = false;
				this.saveToStorage();
			}
		},
		setLoading(loading) {
			this.loading = loading;
		},
		setDnd(chatInfo, isDnd) {
			let chat = this.findChat(chatInfo);
			if (chat) {
				chat.isDnd = isDnd;
			}
		},
		setTop(chatInfo, isTop) {
			let chat = this.findChat(chatInfo);
			if (chat) {
				chat.isTop = isTop;
				chat.cloudIsTop = isTop;
				chat.stored = false;
				this.moveTop(chatInfo);
				this.saveToStorage();
			}
		},
		refreshChats() {
			let chats = cacheChats ? cacheChats.filter(chat => isCloudChat(chat)) : this.chats;
			// 更新会话置顶和免打扰状态
			const friendStore = useFriendStore();
			const groupStore = useGroupStore();
			chats.forEach(chat => {
				if (chat.type == 'PRIVATE') {
					let friend = friendStore.findFriend(chat.targetId);
					if (friend) {
						chat.targetId = friend.id || chat.targetId;
						chat.conversationId = chat.conversationId || yunxinIM.getConversationId(friend.imAccid || friend.accountId || chat.targetId, 'p2p');
						chat.showName = chat.showName || friend.showNickName;
						chat.headImage = chat.headImage || friend.headImage;
						chat.isDnd = friend.isDnd;
						chat.isTop = chat.cloudIsTop ?? chat.isTop ?? friend.isTop;
						chat.companyName = friend.companyName
					}
				} else if (chat.type == 'GROUP') {
					let group = groupStore.findGroup(chat.targetId);
					if (group) {
						chat.targetId = group.id || chat.targetId;
						chat.showName = chat.showName || group.showGroupName || group.name;
						chat.headImage = chat.headImage || group.headImageThumb || group.headImage;
						chat.isDnd = group.isDnd;
						chat.isTop = chat.cloudIsTop ?? chat.isTop ?? group.isTop;
					}
				}
			})
			this.fillPrivateProfiles(chats);
			chats = this.deduplicateChats(chats);
			// 排序
			chats.sort((chat1, chat2) => {
				if (chat1.isTop && !chat2.isTop) {
					return -1;
				} else if (chat2.isTop && !chat1.isTop) {
					return 1;
				} else {
					return getChatSortTime(chat2) - getChatSortTime(chat1)
				}
			});
			/**
			 * 由于部分浏览器不支持websql或indexdb，只能使用localstorage，而localstorage大小只有10m,可能会导致缓存空间溢出
			 * 解决办法:针对只能使用localstorage的浏览器，最多保留1w条消息,每个会话最多保留1000条消息
			 */
			if (localForage.driver().includes("localStorage")) {
				this.fliterMessage(chats, 10000, 1000)
			}
			// 记录热数据索引位置
			chats.forEach(chat => {
				if (!chat.hotMinIdx || chat.hotMinIdx != chat.messages.length) {
					chat.hotMinIdx = chat.messages.length;
					chat.stored = false;
				}
			});
			// 将消息一次性装载回来
			this.chats = chats.filter(chat => !chat.delete && isCloudConversationChat(chat));
			// 清空缓存
			cacheChats = null;
			// 持久化消息
			this.saveToStorage(true);
		},
		fliterMessage(chats, maxTotalSize, maxPerChatSize) {
			// 每个会话只保留maxPerChatSize条消息
			let remainTotalSize = 0;
			chats.forEach(chat => {
				if (chat.messages.length > maxPerChatSize) {
					let idx = chat.messages.length - maxPerChatSize;
					chat.messages = chat.messages.slice(idx);
				}
				remainTotalSize += chat.messages.length;
			})
			// 保证消息总数不超过maxTotalSize条，否则继续清理
			if (remainTotalSize > maxTotalSize) {
				this.fliterMessage(chats, maxTotalSize, maxPerChatSize / 2);
			}
		},
		saveToStorage(withColdMessage) {
			const userStore = useUserStore();
			let userId = userStore.userInfo.id;
			let key = "chats-" + userId;
			let chatKeys = [];
			let deletedChatKeys = [];
			const promises = [];
			// 按会话为单位存储
			for (let idx in this.chats) {
				let chat = this.chats[idx];
				// 只存储有改动的会话
				let chatKey = `${key}-${chat.type}-${chat.targetId}`
				if (!chat.stored) {
					if (chat.delete) {
						const keys = this.getStorageKeysForChat(key, chat);
						keys.forEach(item => {
							deletedChatKeys.push(item);
							promises.push(localForage.removeItem(item))
							promises.push(localForage.removeItem(item + '-hot'))
						})
					} else {
						// 存储冷数据
						if (withColdMessage) {
							let coldChat = Object.assign({}, chat);
							coldChat.messages = chat.messages.slice(0, chat.hotMinIdx);
							promises.push(localForage.setItem(chatKey, coldChat))
							console.log("coldKey:", chatKey)
						}
						// 存储热消息
						let hotKey = chatKey + '-hot';
						let hotChat = Object.assign({}, chat);
						hotChat.messages = chat.messages.slice(chat.hotMinIdx)
						promises.push(localForage.setItem(hotKey, hotChat))
						console.log("hotKey:", hotKey)
					}
					chat.stored = true;
				}
				if (!chat.delete) {
					chatKeys.push(chatKey);
				}
			}
			this.chats = this.chats.filter(chat => !chat.delete);
			// 加载中只更新内存列表，不写入本地缓存，防止卡顿
			if (this.loading) {
				return;
			}
			// 会话核心信息
			let chatsData = {
				privateMsgMaxId: this.privateMsgMaxId,
				groupMsgMaxId: this.groupMsgMaxId,
				systemMsgMaxSeqNo: this.systemMsgMaxSeqNo,
				chatKeys: chatKeys
			}
			Promise.all(promises).then(() => {
				localForage.setItem(key, chatsData)
			}).catch(() => {
				console.log("本地消息缓存存储失败")
			})
			// 清理已删除的会话
			this.chats = this.chats.filter(chat => {
				return !chat.delete && !deletedChatKeys.includes(`${key}-${chat.type}-${chat.targetId}`);
			})
		},
		getStorageKeysForChat(baseKey, chat) {
			if (chat.type !== 'PRIVATE') {
				return [`${baseKey}-${chat.type}-${chat.targetId}`];
			}
			return [...getFriendAliases(chat.targetId)].map(targetId => `${baseKey}-${chat.type}-${targetId}`);
		},
		deduplicateChats(chats = []) {
			return chats.reduce((list, chat) => {
				const idx = list.findIndex(item => isSameChat(item, chat));
				if (idx < 0) {
					list.push(chat);
					return list;
				}
				if (Number(chat.optTime || 0) > Number(list[idx].optTime || 0)) {
					list[idx] = {
						...list[idx],
						...chat,
						cloudIsTop: chat.cloudIsTop ?? list[idx].cloudIsTop,
						isTop: chat.cloudIsTop ?? list[idx].cloudIsTop ?? chat.isTop ?? list[idx].isTop
					};
				}
				return list;
			}, []);
		},
		clear() {
			// 【关键修复】退出前先强制保存所有未保存的消息
			if (this.chats && this.chats.length > 0) {
				console.log('[chatStore.clear] 退出前强制保存消息，会话数量:', this.chats.length);
				// 标记所有会话为未保存状态，然后强制保存
				this.chats.forEach(chat => {
					chat.stored = false;
				});
				// 同步保存到本地存储（使用 withColdMessage=true 确保所有消息都保存）
				this.saveToStorage(true);
			}
			
			cacheChats = []
			cloudConversationIds = new Set();
			this.activeChat = null;
			this.chats = [];
			this.privateMsgMaxId = 0;
			this.groupMsgMaxId = 0;
			this.systemMsgMaxSeqNo = 0;
			this.loading = false;
		},
		async loadChat() {
			const chats = await yunxinIM.getCloudConversationList();
			this.chats = [];
			this.activeChat = null;
			this.privateMsgMaxId = 0;
			this.groupMsgMaxId = 0;
			this.systemMsgMaxSeqNo = 0;
			cloudConversationIds = new Set(chats.map(chat => chat.conversationId).filter(Boolean).map(id => String(id)));
			this.chats = chats.filter(chat => !chat.delete && isCloudConversationChat(chat));
			cacheChats = null;
			this.saveToStorage(true);
		}
	},
	getters: {
		findChats: (state) => () => {
			if (cacheChats && state.loading) {
				return cacheChats;
			}
			return state.chats;
		},
		findChatIdx: (state) => (chat) => {
			let chats = state.findChats();
			for (let idx in chats) {
				if (isSameChat(chats[idx], chat)) {
					chat = chats[idx];
					return idx
				}
			}
		},
		findChat: (state) => (chat) => {
			let chats = state.findChats();
			let idx = state.findChatIdx(chat);
			return idx >= 0 ? chats[idx] : null;
		},
		findChatByFriend: (state) => (fid) => {
			let chats = state.findChats();
			return chats.find(chat => isSameChat(chat, { type: 'PRIVATE', targetId: fid }))
		},
		findChatByGroup: (state) => (gid) => {
			let chats = state.findChats();
			return chats.find(chat => chat.type == 'GROUP' &&
				chat.targetId == gid)
		},
		findMessage: (state) => (chat, msgInfo) => {
			if (!chat) {
				return null;
			}
			if (chat.type == 'SYSTEM') {
				// 系统消息通过seqNo判断
				for (let idx in chat.messages) {
					if (msgInfo.seqNo && chat.messages[idx].seqNo == msgInfo.seqNo) {
						return chat.messages[idx];
					}
				}
			} else {
				// 通过id判断
				if (msgInfo.id) {
					for (let idx = chat.messages.length - 1; idx >= 0; idx--) {
						let m = chat.messages[idx];
						if (m.id && msgInfo.id == m.id) {
							return m;
						}
						// 如果id比要查询的消息小，说明没有这条消息
						if (m.id && m.id < msgInfo.id) {
							break;
						}
					}
				}
				// 正在发送中的临时消息可能没有id,只有tmpId
				if (msgInfo.selfSend && msgInfo.tmpId) {
					for (let idx = chat.messages.length - 1; idx >= 0; idx--) {
						let m = chat.messages[idx];
						if (!m.selfSend || !m.tmpId) {
							continue;
						}
						if (msgInfo.tmpId == m.tmpId) {
							return m;
						}
						// 如果id比要查询的消息小，说明没有这条消息
						if (m.tmpId && m.tmpId < msgInfo.tmpId) {
							break;
						}
					}
				}
			}
		},
		topSize: (state) => () => {
			let chats = state.findChats();
			return chats.filter(chat => chat.isTop).length;
		}
	}
});
